import type { Feature, Geometry } from "@turf/helpers";
import type { GeometryCollection } from "geojson";
import { useCallback } from "react";
import { geminiRequest } from "senf-shared";
import i18n from "senf-shared/utils/i18n";
import { cleanAndParseAiJson } from "senf-shared/utils/reinsertionHelpers";
import { v4 as uuidv4 } from "uuid";
import type { SurveyQuestionProps } from "./SurveyQuestion";
import { pageTypeConfig } from "./pageTypes";
import type {
  Question,
  QuestionBaseProps,
  QuestionOptionsKey,
  Result,
} from "./survey.types";

export const toggle = (
  previous: string | string[],
  val: string,
  type: string,
) => {
  if (type === "radio") return val;
  if (!Array.isArray(previous)) return [val];
  return previous?.includes(val)
    ? previous?.filter((c: string) => c !== val)
    : [...previous, val];
};
export const isChecked = (
  current: QuestionBaseProps["value"],
  val: string,
  type: string,
) => {
  if (type === "radio") return current === val;
  if (typeof current?.includes === "function") return current?.includes(val);
  return false;
};
export const useEditQuestion = (
  { pageIndex, setPages }: Pick<SurveyQuestionProps, "pageIndex" | "setPages">,
  listeners?: React.DependencyList,
) => {
  const focusLast = <T extends keyof Question>(value: Question[T]) => {
    setTimeout(() => {
      if (!value?.length) return;
      const newField = document.getElementById(
        value[value.length - 1]?.uid,
      ) as HTMLTextAreaElement;

      newField?.dispatchEvent(new CustomEvent("programmaticFocus"));
    });
  };

  const updateQuestion = useCallback(
    <T extends keyof Question>(value: Question[T], key: T) => {
      if (!key || !setPages) return;
      setPages((prev) => {
        const pages = [...prev];
        if (value === undefined) {
          delete pages[pageIndex][key];
        } else {
          pages[pageIndex][key] = value;
        }
        return pages;
      });
    },
    [pageIndex, setPages, ...(listeners || [])],
  );

  const updateOption = (
    value: string,
    key: QuestionOptionsKey,
    uid: string,
    valueKey = "value",
  ) => {
    if (!key || !setPages) return;
    setPages((prev) => {
      const pages = [...prev];
      if (value === undefined) {
        delete pages[pageIndex][key];
      } else {
        pages[pageIndex][key] = pages[pageIndex][key]?.map((item) => {
          if (item.uid === uid) return { ...item, [valueKey]: value };
          return item;
        });
      }
      return pages;
    });
  };

  /**
   * @param focus when focus is true, the input field needs an id with the uid
   */
  const addItem = (
    key: QuestionOptionsKey,
    options?: { value?: string | null; focus?: boolean; maxItems?: boolean },
  ) => {
    if (!key || !setPages) return;

    setPages((prev) => {
      return prev.map((page, idx) => {
        if (idx !== pageIndex) return page;
        if (options?.maxItems && maxItems(page, key, page.qtype)) return page;

        // Ensure immutability by spreading
        const updatedPage = { ...page };
        const updatedKeyArray = [...(updatedPage[key] || [])];

        // Generate a unique ID inside the updater function
        updatedKeyArray.push({
          value: options?.value ?? "",
          uid: uuidv4(),
        });

        updatedPage[key] = updatedKeyArray;

        if (options?.focus) focusLast(updatedKeyArray);

        return updatedPage;
      });
    });
  };

  /**
   * @param focus when focus is true, the input field needs an id with the uid
   */
  const removeItem = (
    uid: string,
    key: QuestionOptionsKey,
    options?: { focus?: boolean },
  ) => {
    if (!key || !setPages) return;
    setPages((prev) => {
      const pages = [...prev];
      const items = pages[pageIndex][key] || [];

      if (uid === "other") pages[pageIndex].otherOption = false;

      const index = items.findIndex((item) => item.uid === uid);
      if (index > -1) pages[pageIndex][key]?.splice(index, 1);

      if (options?.focus) focusLast(items);
      return pages;
    });
  };

  const updateText = <T = string>(
    text: T,
    id: string,
    key: keyof Question,
    itemIndex?: number,
    textKey = "value",
  ) => {
    if (!key || !setPages) return;
    setPages((prev) => {
      return prev.map((page, i) => {
        if (pageIndex !== i) return page;

        // Create a new array with all items deeply copied
        const items = (page[key] || []).map((item) => ({ ...item }));
        const index = itemIndex ?? items.findIndex((item) => item.uid === id);

        if (index < 0) return page;

        // Create new item with updated text
        const updatedItem = {
          ...items[index],
          [textKey]: text,
        };

        // Create new items array with updated item
        const updatedItems = [
          ...items.slice(0, index),
          updatedItem,
          ...items.slice(index + 1),
        ];

        // Return new page object with updated items
        return {
          ...page,
          [key]: updatedItems,
        };
      });
    });
  };

  const generateItems = async (
    key: QuestionOptionsKey,
    focus,
    page,
    generateType,
  ) => {
    if (!key || !setPages) return;

    console.log(page);

    const response = await geminiRequest({
      body: `
      
      Question title ${page.qtext}
      ${page.qbody ? `Question description: ${page.qbody}` : ""}
      ${generateType === "more" && page.options ? `Already set options: ${page.options.map((option) => option.value).join(", ")}` : ""}
      `,
      assistantId: "generateOptions",
      lang: i18n?.resolvedLanguage,
    });

    const optionsArray = cleanAndParseAiJson(response);

    const formattedOptions = optionsArray.map((option, index) => ({
      value: option,
      uid: uuidv4(),
    }));

    console.log("formattedOptions", formattedOptions);

    if (formattedOptions.length === 0) return;

    setPages((prev) => {
      return prev.map((page, index) => {
        if (index !== pageIndex) return page;

        const updatedPage = {
          ...page,
          [key]: formattedOptions,
        };

        if (focus) {
          focusLast(updatedPage[key]);
        }

        return updatedPage;
      });
    });
  };

  return {
    updateQuestion,
    updateOption,
    addItem,
    removeItem,
    updateText,
    generateItems,
  };
};
interface MapInputProps {
  number: number;
  drawType: "point" | "path" | "area";
  choices?: { value: string; uid: string }[];
  description?: string;
}
export const validMapInput = (
  features: Feature<Geometry | GeometryCollection, MapInputProps>,
  type: Question["mapbasedQtype"],
) => {
  return Object.values(features).every((value) => {
    const { description, choices } = value?.properties || {};
    const validText = type === "textarea" && description && description !== "";
    const validChoice = type === "radio" && choices;

    return validText || validChoice;
  });
};

export function processFirebaseUndefinedError(message: string) {
  console.error(message);
  const isUndefined = /(?<=Unsupported field value: ).*?(?= )/gm;
  const key = /(?<=found in field ).*?(?= in document)/gm;
  const fieldName = new RegExp(key).exec(message)?.[0];
  if (message && new RegExp(isUndefined).exec(message)?.[0] === "undefined") {
    return `Error: ${fieldName || "Ein Feld"} ist undefined.`;
  }
  return message;
}

export function findSmallerStackX(data: Question["imgUrl"]) {
  const base = [
    { x: 0, h: 1 },
    { x: 1, h: 1 },
  ];
  if (data) base.push(...data);
  const reducedData = base.reduce(
    (acc, { x = 0, h }) => {
      acc[x] = (acc[x] || 0) + h;
      return acc;
    },
    {} as Record<string | number, number>,
  );
  const minX = Object.keys(reducedData).reduce(
    (minX, x) => (reducedData[x] < reducedData[minX] ? x : minX),
    "0",
  );
  return {
    x: Number.parseInt(minX, 10),
    h: reducedData[minX] || Number.POSITIVE_INFINITY,
  };
}

export const getMostRecentlyViewedPageIndex = (
  pages: Question[],
  result?: Result["values"],
  order?: number,
  offset = 0,
) => {
  if (
    !result ||
    !Object.keys(result).length ||
    Object.entries(result).every(([, value]) => !value?.viewed)
  )
    return -1;
  const sortedResults = Object.entries(result)
    .filter(([, value]) => !!value?.viewed)
    .map(([id, value]) => ({
      id,
      viewedTime: value?.viewed ? new Date(value.viewed).getTime() : 0,
    }))
    .sort((a, b) => (a.viewedTime > b.viewedTime ? -1 : 1));

  const { id } = sortedResults[offset] || {};

  const index = id
    ? Math.max(
        0,
        pages.findIndex(({ uid }) => uid === id),
      )
    : (order || 1) - 1;

  return index;
};

export const maxItems = (
  question: Question,
  key: QuestionOptionsKey,
  qtype: Question["qtype"],
) => {
  const max =
    pageTypeConfig[qtype].defaults?.[
      key === "options"
        ? "maxOptions"
        : key === "columns"
          ? "maxColumns"
          : "maxLabels"
    ];
  return (question[key]?.length || Number.POSITIVE_INFINITY) >= max
    ? max
    : false;
};
